import { useEffect, useState } from 'react'
import { useLingui } from '@lingui/react'
import { msg, Trans, t } from '@lingui/macro'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
  useTheme,
  Stack,
  Tooltip,
} from '@mui/material/'
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  gridClasses,
} from '@mui/x-data-grid'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { randStr } from '../../utils'
import SvgCustomIcon from "../icons/SvgCustomIcon";
import {StripedDataGrid} from "./StripedDataGrid";

const paginationModel = { page: 0, pageSize: 25 }


export default function OrganizationsTable({
                                             organizations,
                                             addOrModifyOrganization,
                                             deleteOrganization,
                                           }) {
  const theme = useTheme()
  const { _ } = useLingui()
  const [rows, setRows] = useState([])
  const [rowModesModel, setRowModesModel] = useState({})
  const [idToDelete, setIdToDelete] = useState(null)

  useEffect(() => {
    if (organizations.length)
      setRows(organizations.map(org => ({ ...org, value: org.id })))
  }, [organizations])

  // ✳️ stesso handler del vecchio toolbar
  const handleAddOrganization = () => {
    const id = randStr()
    setRows(old => [
      {
        id,
        description: '',
        short_description: '',
        basic_id: -1,
        isNew: true,
      },
      ...old,
    ])
    setRowModesModel(oldModel => ({
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'description' },
      ...oldModel,
    }))
  }

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const handleDeleteClick = id => () => {
    deleteOrganization(id)
    setRows(rows.filter(row => row.id !== id))
  }

  const handleEditClick = id => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveClick = id => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelClick = id => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    })

    const editedRow = rows.find(row => row.id === id)
    if (editedRow.isNew) {
      setRows(rows.filter(row => row.id !== id))
    }
  }

  const processRowUpdate = newRow => {
    addOrModifyOrganization(newRow)
    const updatedRow = { ...newRow, isNew: false }
    setRows(rows.map(row => (row.id === newRow.id ? updatedRow : row)))
    return updatedRow
  }

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel)
  }

  const handleOpenDialog = id => () => {
    setIdToDelete(id)
  }

  const handleCloseDialog = () => {
    setIdToDelete(null)
  }

  const columns = [
    {
      field: 'id',
      headerName: _(msg`ID`),
      headerClassName: 'overload-header',
      headerAlign: 'center',
      align: 'center',
      flex: 0.3,
      valueFormatter: value =>
          Number.isInteger(value) ? value : _(msg`TBD`),
    },
    {
      field: 'description',
      headerName: _(msg`Official name`),
      headerClassName: 'overload-header',
      editable: true,
      flex: 1.5,
    },
    {
      field: 'short_description',
      headerName: _(msg`Short name`),
      headerClassName: 'overload-header',
      editable: true,
      flex: 0.5,
    },
    {
      field: 'basic_id',
      headerName: _(msg`Permits`),
      headerClassName: 'overload-header',
      type: 'singleSelect',
      editable: true,
      flex: 1,
      valueOptions: [
        { label: t`Input as police`, value: 1 },
        { label: t`Input as healthcare`, value: 2 },
        { label: t`Input as insurance`, value: 3 },
        { label: t`Analysis only`, value: -1 },
      ],
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: _(msg`Actions`),
      headerClassName: 'overload-header',
      cellClassName: 'actions',
      flex: 0.5,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
                icon={<SvgCustomIcon name={'save'} />}
                label={_(msg`Save`)}
                sx={{ color: 'primary.main' }}
                onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
                icon={<SvgCustomIcon name={'cancel'} />}
                label={_(msg`Cancel`)}
                onClick={handleCancelClick(id)}
                color="inherit"
            />,
          ]
        }

        if (id > 3) {
          return [
            <GridActionsCellItem
                icon={<SvgCustomIcon name={'edit'} color={theme.palette.newColors.gray} />}
                label={_(msg`Edit`)}
                onClick={handleEditClick(id)}
                color="inherit"
            />,
            <GridActionsCellItem
                icon={<SvgCustomIcon name={'delete'} color={theme.palette.newColors.gray} />}
                label={_(msg`Delete`)}
                onClick={handleOpenDialog(id)}
                color="inherit"
            />,
          ]
        }

        return [
          <GridActionsCellItem
              icon={<SvgCustomIcon color={theme.palette.newColors.gray} name={'edit'} />}
              label={_(msg`Edit`)}
              onClick={handleEditClick(id)}
              color="inherit"
          />,
        ]
      },
    },
  ]

  return (
      <Box
          width="100%"
          sx={{
            '& .overload-header': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
      >
        {/* 🔝 PULSANTE ESTERNO AL DATAGRID */}
        <Stack direction="row" justifyContent="flex-start" mb={1}>
          <Button
              startIcon={<AddCircleIcon />}
              variant="contained"
              onClick={handleAddOrganization}
              sx={{
                background: theme.palette.newColors.pinkGradient,
                color: 'white',
                '&:hover': {
                  background: theme.palette.newColors.pinkGradient,
                  opacity: 0.9,
                },
              }}
          >
            <Trans>Add new Organization</Trans>
          </Button>
        </Stack>

        <StripedDataGrid
            showColumnVerticalBorder
            showCellVerticalBorder
            rows={rows}
            columns={columns}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            initialState={{
              pagination: { paginationModel },
              columns: {
                columnVisibilityModel: { isNew: false },
              },
            }}
            hideFooter={rows.length <= paginationModel.pageSize}
            pageSizeOptions={[paginationModel.pageSize]}
            getRowClassName={params =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
        />

        {/* Dialog conferma eliminazione */}
        <Dialog open={!!idToDelete} onClose={handleCloseDialog}>
          <DialogTitle>
            <Trans>Confirm deletion</Trans>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              <Trans>Are you sure? This action can’t be undone!</Trans>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCloseDialog}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
                variant="outlined"
                color="error"
                onClick={handleDeleteClick(idToDelete)}
            >
              <Trans>Yes</Trans>
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  )
}
